import React from 'react';
import styled from 'styled-components';

const PreviewText = styled.p`
  color: var(--color-text-light);
  margin-bottom: var(--spacing-3);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

interface ProductPreviewTextProps {
  text: string;
  maxLength?: number;
}

const ProductPreviewText: React.FC<ProductPreviewTextProps> = ({ 
  text, 
  maxLength = 100 
}) => {
  if (!text || text.trim().length === 0) {
    return null;
  }

  // Smart truncation that respects sentence boundaries
  const smartTruncate = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }

    // First, try to find a sentence break within the limit
    const sentences = text.split(/[.!?]+/);
    let result = '';
    
    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i].trim();
      if (sentence.length === 0) continue;
      
      const withSentence = result + (result ? '. ' : '') + sentence;
      if (withSentence.length <= maxLength - 3) { // Leave room for ...
        result = withSentence;
      } else {
        break;
      }
    }

    // If we got a good result, use it
    if (result.length > maxLength * 0.6) { // At least 60% of desired length
      return result + (result.endsWith('.') ? '' : '...');
    }

    // Otherwise, truncate at word boundary
    const words = text.split(' ');
    result = '';
    
    for (const word of words) {
      const withWord = result + (result ? ' ' : '') + word;
      if (withWord.length <= maxLength - 3) {
        result = withWord;
      } else {
        break;
      }
    }

    return result + '...';
  };

  // Clean up the text first
  const cleanText = text
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/[^\w\s.!?,:;åäöÅÄÖ-]/g, '') // Remove special chars except basic punctuation
    .trim();

  const truncatedText = smartTruncate(cleanText, maxLength);

  return (
    <PreviewText>
      {truncatedText}
    </PreviewText>
  );
};

export default ProductPreviewText;